/**
 * Generate code snippets for local server deployment
 */

import { WorkflowParameter, WorkflowImage } from "./types";

export function generateLocalCode(
    workflowName: string,
    workspaceUrl: string,
    apiKey: string,
    workflowParameters: WorkflowParameter[],
    workflowImages: WorkflowImage[],
    localDeployUrl: string,
    language: string
): string {
    const curlImageString = workflowImages
        .map((image) => `        "${image.name}": {"type": "url", "value": "IMAGE_URL"}`)
        .join(",\n");

    const curlParameterString = workflowParameters
        .map((param) => {
            const value =
                param.type === "boolean"
                    ? param.parsed_value
                        ? "true"
                        : "false"
                    : JSON.stringify(param.parsed_value);
            return `        "${param.name}": ${value}`;
        })
        .join(",\n");

    const javascriptImageString = curlImageString.replace("        ", "    ");
    const javascriptParameterString = curlParameterString.replace("        ", "            ");

    const pythonImageString = workflowImages
        .map((image) => `        "${image.name}": "YOUR_IMAGE.jpg"`)
        .join(",\n");

    const pythonParameterString = workflowParameters
        .map((param) => {
            const value =
                param.type === "boolean"
                    ? param.parsed_value
                        ? "True"
                        : "False"
                    : JSON.stringify(param.parsed_value);
            return `        "${param.name}": ${value}`;
        })
        .join(",\n");

    let divider = "";
    if (curlImageString && curlParameterString) {
        divider = ",";
    }

    const endpoint = `${localDeployUrl}/${workspaceUrl}/workflows/${workflowName}`;

    const curlCode = `
\`\`\`bash
curl --location '${endpoint}' \\
--header 'Content-Type: application/json' \\
--data '{
    "api_key": "${apiKey}",
    "inputs": {${curlImageString ? "\n" + curlImageString + divider : ""}${
        curlParameterString ? "\n" + curlParameterString : ""
    }
    }
}'
\`\`\`
`;

    const javascriptCode = `
\`\`\`javascript
const response = await fetch('${endpoint}', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        api_key: '${apiKey}',
        inputs: {${javascriptImageString ? "\n" + javascriptImageString + divider : ""}${
            javascriptParameterString ? "\n" + javascriptParameterString : ""
        }
        }
    })
});

const result = await response.json();
console.log(result);
\`\`\`
`;

    const pythonCode = `
\`\`\`python
# 1. Import the library
from inference_sdk import InferenceHTTPClient

# 2. Connect to your local server
client = InferenceHTTPClient(
    api_url="${localDeployUrl}", # Local server address
    api_key="${apiKey}"
)

# 3. Run your workflow on an image
result = client.run_workflow(
    workspace_name="${workspaceUrl}",
    workflow_id="${workflowName}"${
        workflowImages.length > 0
            ? `,
    images={
${pythonImageString} # Path to your image file
    }`
            : ""
    }${
        pythonParameterString
            ? `,
    parameters={
${pythonParameterString}
    }`
            : ""
    },
    use_cache=True # Speeds up repeated requests
)

# 4. Get your results
print(result)
\`\`\`
`;

    if (language === "curl") {
        return curlCode;
    } else if (language === "python") {
        return pythonCode;
    } else if (language === "javascript") {
        return javascriptCode;
    }
    return pythonCode;
}
